import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, SearchIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { Reveal } from '../components/ui/Reveal';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { CtaBand } from '../components/ui/CtaBand';
import { articles, newsCategories } from '../data/articles';
const PAGE_SIZE = 4;
export function News() {
  const [category, setCategory] = useState('Latest');
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [featured, ...others] = articles;
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return others.filter(article => {
      const matchesCategory = category === 'Latest' || article.category === category;
      const matchesQuery = !term || article.title.toLowerCase().includes(term) || article.standfirst.toLowerCase().includes(term);
      return matchesCategory && matchesQuery;
    });
  }, [category, query, others]);
  const reset = () => {
    setCategory('Latest');
    setQuery('');
    setVisible(PAGE_SIZE);
  };
  return <>
      <PageHero eyebrow="Newsroom" title="Campaign news, statements and speeches." intro="Everything the campaign publishes, in one place — with the full text of every statement and address." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'News'
    }]} data-id="element-1107" data-mp="38zh5cad75vur" />

      <section className="bg-sand-50 py-14 lg:py-20" aria-labelledby="featured-heading" data-id="element-1108" data-mp="2v8azzxn8l3zy">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-1109" data-mp="2qbkjxabfm8ej">
          <h2 id="featured-heading" className="sr-only" data-id="element-1110" data-mp="2lc7xgqoh33ch">
            Featured story
          </h2>
          <Link to={`/news/${featured.slug}`} className="group grid gap-8 rounded-media border border-sand-200 bg-white p-6 transition-[border-color,box-shadow] duration-200 ease-emphasis hover:border-green-200 hover:shadow-card lg:grid-cols-12 lg:p-8" data-id="element-1111" data-mp="2hamc0gxvhgqo">
            <span className="overflow-hidden rounded-card lg:col-span-6" data-id="element-1112" data-mp="26or6nq7npelm">
              <img src={featured.image} alt="" className="aspect-[16/10] w-full object-cover transition-transform duration-300 ease-emphasis group-hover:scale-[1.02]" data-id="element-1113" data-mp="26mo4y8x55x12" />
            </span>
            <span className="flex flex-col justify-center lg:col-span-6" data-id="element-1114" data-mp="2leyc95ckf34w">
              <span className="flex items-center gap-3 text-[12px] font-bold uppercase tracking-[0.12em]" data-id="element-1115" data-mp="2qcqd4ov6tzl8">
                <span className="rounded bg-green-50 px-2 py-1 text-green-700" data-id="element-1116" data-mp="2bolwdqfp3y53">
                  {featured.category}
                </span>
                <span className="text-slate-500" data-id="element-1117" data-mp="2glly5p5iieqe">{featured.date}</span>
              </span>
              <span className="mt-4 font-display text-[26px] leading-snug text-ink sm:text-[32px]" data-id="element-1118" data-mp="34b6m6mgiw26j">
                {featured.title}
              </span>
              <span className="mt-3 text-[16px] leading-relaxed text-slate-600" data-id="element-1119" data-mp="xo909596tk3o">
                {featured.standfirst}
              </span>
              <span className="mt-5 inline-flex items-center gap-2 text-[15px] font-semibold text-green-700" data-id="element-1120" data-mp="sommmupg61uc">
                Read in full
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-200 ease-emphasis group-hover:translate-x-1" data-id="element-1121" data-mp="16infj7tpwums" />
              </span>
            </span>
          </Link>
        </div>
      </section>

      <section className="bg-white py-14 lg:py-20" aria-labelledby="all-news-heading" data-id="element-1122" data-mp="9sebxbdo0yrn">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-1123" data-mp="nrw7qapdpcai">
          <h2 id="all-news-heading" className="font-display text-[28px] text-ink" data-id="element-1124" data-mp="iqyorvwjoe7y">
            All updates
          </h2>

          <div className="mt-6 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between" data-id="element-1125" data-mp="1z46uo0hfye71">
            <div className="relative w-full lg:max-w-sm" data-id="element-1126" data-mp="1u5vhpgvunnkl">
              <label htmlFor="news-search" className="sr-only" data-id="element-1127" data-mp="2ph3ee0dsmo9h">
                Search news
              </label>
              <SearchIcon aria-hidden className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-500" data-id="element-1128" data-mp="2uev72wm9ieeq" />
              <input id="news-search" type="search" value={query} onChange={event => {
              setQuery(event.target.value);
              setVisible(PAGE_SIZE);
            }} placeholder="Search updates" className="w-full rounded-lg border border-sand-300 bg-white py-3 pl-11 pr-4 text-[15px] text-ink placeholder:text-slate-500/70 focus:border-green-600 focus:outline-none" data-id="element-1129" data-mp="34edph8mixs4o" />
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by category" data-id="element-1130" data-mp="3s4q22wspsd1l">
              {newsCategories.map(item => {
              const active = category === item;
              return <button key={item} type="button" aria-pressed={active} onClick={() => {
                setCategory(item);
                setVisible(PAGE_SIZE);
              }} className={`rounded-lg border px-3.5 py-2 text-[14px] font-medium transition-colors duration-150 ease-emphasis ${active ? 'border-green-600 bg-green-600 text-white' : 'border-sand-300 text-slate-600 hover:border-slate-500/50 hover:text-ink'}`} data-id="element-1131" data-mp="1kizss52k03lg">
                    {item}
                  </button>;
            })}
            </div>
          </div>

          {filtered.length === 0 ? <div className="mt-8" data-id="element-1132" data-mp="2my8b3jnbvpft">
              <EmptyState title="No updates match your filters" description="Try another category, or search for a term such as security, power or volunteers." actionLabel="Clear filters" onAction={reset} data-id="element-1133" data-mp="2i4x33f6s52mb" />
            </div> : <>
              <ul className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-1134" data-mp="6hk6xfaglt5p">
                {filtered.slice(0, visible).map((article, index) => <li key={article.slug} data-id="element-1135" data-mp="3spjrwiykzn6d" data-mpi={index}>
                    <Reveal delay={Math.min(index, 5) * 0.03} className="h-full" data-id="element-1136" data-mp="3soihmgpe34pp">
                      <Link to={`/news/${article.slug}`} className="group flex h-full flex-col overflow-hidden rounded-card border border-sand-200 transition-[border-color,box-shadow] duration-200 ease-emphasis hover:border-green-200 hover:shadow-card" data-id="element-1137" data-mp="1k1uvg15axth">
                        <span className="overflow-hidden" data-id="element-1138" data-mp="1hxs04q403s4p">
                          <img src={article.image} alt="" loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-300 ease-emphasis group-hover:scale-[1.02]" data-id="element-1139" data-mp="1mwv40788vsb3" />
                        </span>
                        <span className="flex flex-1 flex-col p-5" data-id="element-1140" data-mp="25uew55kmvg7y">
                          <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.1em]" data-id="element-1141" data-mp="210ao0izmt25a">
                            <span className="text-green-700" data-id="element-1142" data-mp="2fsuofbvecvj1">{article.category}</span>
                            <span className="text-slate-500" data-id="element-1143" data-mp="2ar5drxc36jvh">{article.date}</span>
                          </span>
                          <span className="mt-2 font-display text-[19px] leading-snug text-ink" data-id="element-1144" data-mp="2opbz6dzumre9">
                            {article.title}
                          </span>
                          <span className="mt-2 text-[14px] leading-relaxed text-slate-600" data-id="element-1145" data-mp="ue6sf0tgtyup">
                            {article.standfirst}
                          </span>
                          <span className="mt-auto inline-flex items-center gap-2 pt-5 text-[13px] font-semibold text-green-700" data-id="element-1146" data-mp="1exjmhm7vtym0">
                            Read more
                            <ArrowRightIcon aria-hidden className="h-3.5 w-3.5 transition-transform duration-200 ease-emphasis group-hover:translate-x-1" data-id="element-1147" data-mp="1omjntc9ux63d" />
                          </span>
                        </span>
                      </Link>
                    </Reveal>
                  </li>)}
              </ul>

              {visible < filtered.length && <div className="mt-10 flex justify-center" data-id="element-1148" data-mp="14x9au61hwz8y">
                  <Button variant="secondary" onClick={() => setVisible(visible + PAGE_SIZE)} data-id="element-1149" data-mp="19xx99cs20id7">
                    Load more updates
                  </Button>
                </div>}
            </>}
        </div>
      </section>

      <CtaBand title="Get campaign updates first." description="Policy releases, statements and town halls near you — no more than three messages a week." primary={{
      label: 'Join campaign updates',
      to: '/join'
    }} secondary={{
      label: 'Media centre',
      to: '/media'
    }} data-id="element-1150" data-mp="2lewyu0n0oj3j" />
    </>;
}